import { FC, useEffect, useState } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { IonButton } from '@ionic/react';
import { useSubscriptionV2 } from '../../support/state/useSubscriptionV2';
import { ActiveCaremapNoteEditor } from './ActiveCaremapNoteEditor';
import { AlertPresenter } from '../../AlertBinder';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { ToastPresenter } from '../../ToastBinder';

interface IEditNoteDrawerProps {
    noteId: string;
    trackingFrom?: string;
    hasPrivateCheckbox?: boolean;
};

export class EditNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IEditNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IEditNoteDrawerProps>){}

    mode = 'edit-note';

    Component = EditNoteDrawerView;

    open = (props: IEditNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    close = () => {
        this.drawerHelper.close();
    };
};

export const EditNoteDrawerView: FC<IEditNoteDrawerProps> = ({ noteId, hasPrivateCheckbox, trackingFrom }) => {
    const alert = useInstance(AlertPresenter);

    const history = useInstance(HistoryViewModel);
    const tracking = useInstance(MixPanelEventEmitter);
    const toastPresenter = useInstance(ToastPresenter);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const noteVM = useSubscriptionV2(async () => await notesFactory.createEditForm(noteId));

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const [initialPrivacy, setInitialPrivacy] = useState<boolean>();
    useEffect(() => {
        if (noteVM) {
            setInitialPrivacy(noteVM.getNote().isPrivate);
        }
    }, [noteVM]);

    const saveAndClose = async () => {
        if (!noteVM){
            return;
        }
        try {
            await noteVM.save({ trackingFrom });

            if (initialPrivacy && noteVM.getNote().isPrivate !== initialPrivacy) {
                tracking.track('Note Posted to Community', () => (
                    { 'Method': 'Manually Made Public', 'Is Pinned': notesFactory.notePinsState.isPinned(noteId) }
                ));

                toastPresenter.toast({
                    duration: 3000,
                    message: 'Community post added!',
                    buttons: [{
                        text: 'View Post',
                        handler: () => {
                            history.push('/community-redirect');
                        },
                    }]
                });
            }

            drawerHelper.close();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    if (!noteVM){
        return null;
    }

    const activeCareMap = careMapState.getActiveCaremap();
    if (!activeCareMap){
        return null;
    }

    let title = 'Edit Note';

    const canEdit = careMapState.hasPermissionTo('put-note');
    if (!canEdit){
        title = 'View Note';
    }

    return (
        <QueryParamDrivenDrawerPage
            title={title}
            hasBack={false}
            data-type="note-editor"
            onClose={!canEdit ? drawerHelper.close : undefined}
            buttons={canEdit && (
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        history.back();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteEditor
                noteVM={noteVM}
                trackingFrom={trackingFrom}
                onDeleted={drawerHelper.close}
                hasPrivateCheckbox={hasPrivateCheckbox}
                excludeMenu
            />
        </QueryParamDrivenDrawerPage>
    );
};
